import { PERMISSIONS } from '@/hooks/usePermissions';
import type { DataSource } from './hooks/useReportBuilder';

/** Extra reporting capabilities (UI gating; API base is `reporting.read`). */
export const REPORTING_EXTRA_PERMISSIONS: readonly string[] = [
  PERMISSIONS.REPORTING_GENERATE,
  PERMISSIONS.REPORTING_EXPORT,
  PERMISSIONS.REPORTING_SCHEDULE,
  PERMISSIONS.REPORTING_VIEW_FINANCIALS,
];

/** @deprecated Use REPORTING_EXTRA_PERMISSIONS; kept for tests listing all keys. */
export const REPORTING_PERMISSIONS: readonly string[] = [
  PERMISSIONS.REPORTING_READ,
  ...REPORTING_EXTRA_PERMISSIONS,
];

export type PermissionChecker = {
  isAdmin: boolean;
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
};

/** Sidebar nav link for Reports — matches roles UI "Read". */
export function canShowReportsInNav({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_READ);
}

/** Page access for `/reports`. */
export function canAccessReportsModule({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_READ);
}

export function hasAnyReportingPermission(checker: PermissionChecker): boolean {
  return canAccessReportsModule(checker);
}

/** Saved reports list (names/config metadata). */
export function canViewReportsList({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_READ);
}

/** Execute saved or builder reports. */
export function canRunReports({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_READ);
}

/** Create, edit, delete, and save report configurations. */
export function canGenerateReports({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_READ) && can(PERMISSIONS.REPORTING_GENERATE);
}

export function canExportReports({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_READ) && can(PERMISSIONS.REPORTING_EXPORT);
}

export function canViewReportFinancials({ can }: PermissionChecker): boolean {
  return can(PERMISSIONS.REPORTING_VIEW_FINANCIALS);
}

// ─── Per-module report-group gating (Option A) ───────────────────────────────
// Each report data source maps to the module read permission that governs it.
// A source (report group) is visible only when the user can read that module —
// mirroring how the dashboard charts are gated by their module permission.
// This keeps report visibility consistent with the rest of the app without
// introducing new report-specific permissions.
export const REPORT_SOURCE_PERMISSION: Record<DataSource, string> = {
  contacts: PERMISSIONS.REPORTING_SOURCE_CONTACTS,
  jobs: PERMISSIONS.REPORTING_SOURCE_JOBS,
  billing: PERMISSIONS.REPORTING_SOURCE_BILLING,
  client_ledger: PERMISSIONS.REPORTING_SOURCE_CLIENT_LEDGER,
  time_entries: PERMISSIONS.REPORTING_SOURCE_TIME_ENTRIES,
  wip_entries: PERMISSIONS.REPORTING_SOURCE_WIP,
  capacity: PERMISSIONS.REPORTING_SOURCE_CAPACITY,
  documents: PERMISSIONS.REPORTING_SOURCE_DOCUMENTS,
  users: PERMISSIONS.REPORTING_SOURCE_USERS,
  audit_logs: PERMISSIONS.REPORTING_SOURCE_AUDIT_LOGS,
};

/**
 * Whether the user may see/run reports for a given data source (report group).
 * Admins always may; otherwise the mapped module read permission is required.
 * Sources without a mapping (should not happen) default to visible so a new
 * source is never silently hidden by omission.
 */
export function canAccessReportSource(
  { isAdmin, can }: PermissionChecker,
  source: DataSource,
): boolean {
  if (isAdmin) return true;
  const permission = REPORT_SOURCE_PERMISSION[source];
  if (!permission) return true;
  return can(permission);
}

/**
 * Filters a list of data sources down to those the user may access.
 */
export function filterAllowedReportSources<T extends { value: DataSource }>(
  checker: PermissionChecker,
  sources: readonly T[],
): T[] {
  return sources.filter((s) => canAccessReportSource(checker, s.value));
}
